import React, { useState, useEffect } from 'react';
import '../styles/TimeEditModal.css';

/**
 * TimeEditModal - Modal for editing workout scheduled time
 * Provides options to update just one workout or all strength workouts
 */
function TimeEditModal({ 
  isOpen, 
  onClose, 
  onSaveOne,      // Called when saving time for just this workout
  onSaveAll,      // Called when saving time for all strength workouts
  activity,       // The activity being edited
  activityName    // Display name for the activity
}) {
  // Time state
  const [timeHour, setTimeHour] = useState('');
  const [timeMinute, setTimeMinute] = useState('');
  const [timePeriod, setTimePeriod] = useState('AM');
  const [isSaving, setIsSaving] = useState(false);

  // Parse existing time from activity (24-hour to 12-hour)
  const parseExistingTime = (timeStr) => {
    if (!timeStr) return { hour: '', minute: '', period: 'AM' };
    
    // Handle 24-hour format (HH:mm)
    const match24 = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (match24) {
      let hour = parseInt(match24[1], 10);
      const minute = match24[2];
      const period = hour >= 12 ? 'PM' : 'AM';
      
      if (hour === 0) hour = 12;
      else if (hour > 12) hour = hour - 12;
      
      return { hour: String(hour), minute, period };
    }
    
    // Handle 12-hour format (H:mm AM/PM)
    const match12 = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match12) {
      return { 
        hour: match12[1], 
        minute: match12[2], 
        period: match12[3].toUpperCase() 
      };
    }
    
    return { hour: '', minute: '', period: 'AM' };
  };

  // Initialize time from activity when modal opens
  useEffect(() => {
    if (isOpen && activity) {
      const existingTime = activity.scheduledTime || activity.time;
      const parsed = parseExistingTime(existingTime);
      setTimeHour(parsed.hour);
      setTimeMinute(parsed.minute);
      setTimePeriod(parsed.period);
    }
  }, [isOpen, activity]);

  // Format time to 24-hour format for database
  const formatTime24 = () => {
    if (!timeHour || !timeMinute) return null;
    
    let hour = parseInt(timeHour, 10);
    if (timePeriod === 'PM' && hour !== 12) {
      hour += 12;
    } else if (timePeriod === 'AM' && hour === 12) {
      hour = 0;
    }
    
    return `${String(hour).padStart(2, '0')}:${timeMinute.padStart(2, '0')}`;
  };

  // Handle hour input
  const handleHourChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value === '' || (parseInt(value, 10) >= 1 && parseInt(value, 10) <= 12)) {
      setTimeHour(value);
    }
  };

  // Handle minute input
  const handleMinuteChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value === '' || (parseInt(value, 10) >= 0 && parseInt(value, 10) <= 59)) {
      setTimeMinute(value.padStart(2, '0').slice(-2));
    }
  };

  // Handle save for this one workout
  const handleSaveOne = async () => {
    const time = formatTime24();
    if (!time) return;
    
    setIsSaving(true);
    try {
      await onSaveOne(time);
      onClose();
    } catch (error) {
      console.error('Error saving time:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle save for all strength workouts
  const handleSaveAll = async () => {
    const time = formatTime24();
    if (!time) return;
    
    setIsSaving(true);
    try {
      await onSaveAll(time);
      onClose();
    } catch (error) {
      console.error('Error saving time for all:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const hasValidTime = timeHour && timeMinute;

  return (
    <div className="time-edit-modal-overlay" onClick={handleBackdropClick}>
      <div className="time-edit-modal">
        {/* Header */}
        <div className="time-edit-header">
          <div className="time-edit-title-container">
            <h2 className="time-edit-title">Edit Time</h2>
            <span className="time-edit-subtitle">{activityName || 'Strength Workout'}</span>
          </div>
          <button 
            className="time-edit-close-btn" 
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="time-edit-content">
          {/* Time Input */}
          <div className="time-edit-input-group">
            <label className="time-edit-label">Scheduled Time</label>
            <div className="time-edit-time-inputs">
              <input
                type="text"
                className="time-edit-time-field"
                placeholder="HH"
                value={timeHour}
                onChange={handleHourChange}
                maxLength={2}
                inputMode="numeric"
              />
              <span className="time-edit-time-separator">:</span>
              <input
                type="text"
                className="time-edit-time-field"
                placeholder="MM"
                value={timeMinute}
                onChange={handleMinuteChange}
                maxLength={2}
                inputMode="numeric"
              />
              <div className="time-edit-period-toggle">
                <button
                  type="button"
                  className={`time-edit-period-btn ${timePeriod === 'AM' ? 'active' : ''}`}
                  onClick={() => setTimePeriod('AM')}
                >
                  AM
                </button>
                <button
                  type="button"
                  className={`time-edit-period-btn ${timePeriod === 'PM' ? 'active' : ''}`}
                  onClick={() => setTimePeriod('PM')}
                >
                  PM
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="time-edit-actions">
            <button
              className="time-edit-btn time-edit-btn-secondary"
              onClick={handleSaveOne}
              disabled={!hasValidTime || isSaving}
            >
              {isSaving ? 'Saving...' : 'This One'}
            </button>
            <button
              className="time-edit-btn time-edit-btn-primary"
              onClick={handleSaveAll}
              disabled={!hasValidTime || isSaving}
            >
              {isSaving ? 'Saving...' : 'All Strength Workouts'}
            </button>
          </div>

          <p className="time-edit-hint">
            Choose "This One" to update only this workout, or "All Strength Workouts" to set the same time for all future strength sessions.
          </p>
        </div>
      </div>
    </div>
  );
}

export default TimeEditModal;
